// W-9 Tax Form Viewer Component (Read-only)
import React from 'react';

const W9Viewer = ({ formData }) => {
  if (!formData) {
    return <div className="text-gray-500">No form data available</div>;
  }

  // Handle case where formData might be a string that needs parsing
  let parsedData = formData;
  if (typeof formData === 'string') {
    try {
      parsedData = JSON.parse(formData);
    } catch (e) {
      console.error('Failed to parse form data:', e);
      return <div className="text-red-500">Invalid form data format</div>;
    }
  }

  // Handle nested structure where actual form data is inside a form_data property
  if (parsedData && parsedData.form_data && typeof parsedData.form_data === 'object') {
    parsedData = parsedData.form_data;
  }

  // Helper function to get field value with both camelCase and snake_case support
  const getFieldValue = (camelCase, snakeCase, defaultValue = 'Not provided') => {
    if (!parsedData) return defaultValue;
    return parsedData[camelCase] || parsedData[snakeCase] || defaultValue;
  };

  // Debug: Show what we actually have
  if (process.env.NODE_ENV === 'development') {
    console.log('W9Viewer - Original formData:', formData);
    console.log('W9Viewer - Parsed data:', parsedData);
    console.log('W9Viewer - Data keys:', Object.keys(parsedData || {}));
  }

  // Helper function to get TIN display
  const getTinDisplay = () => {
    const tinType = getFieldValue('tinType', 'tin_type', '');
    const tin = getFieldValue('tin', 'tin', '') || getFieldValue('ssn', 'ssn', '') || getFieldValue('ein', 'ein', '');
    
    if (tinType === 'ssn' && tin) {
      return `SSN: ${tin}`;
    } else if (tinType === 'ein' && tin) {
      return `EIN: ${tin}`;
    } else if (tinType === 'ssn' && parsedData.ssn1 && parsedData.ssn2 && parsedData.ssn3) {
      return `SSN: ${parsedData.ssn1}-${parsedData.ssn2}-${parsedData.ssn3}`;
    } else if (tinType === 'ein' && parsedData.ein1 && parsedData.ein2) {
      return `EIN: ${parsedData.ein1}-${parsedData.ein2}`;
    }
    return 'Not provided';
  };

  // Helper function to get tax classification display
  const getTaxClassificationDisplay = () => {
    const classification = getFieldValue('federalTaxClassification', 'federal_tax_classification', 'individual');
    const llcClassification = getFieldValue('llcClassification', 'llc_classification', 'Not specified');
    const otherDescription = getFieldValue('otherDescription', 'other_description', 'Not specified');
    
    switch (classification) {
      case 'individual':
        return 'Individual/sole proprietor or single-member LLC';
      case 'c-corp':
        return 'C Corporation';
      case 's-corp':
        return 'S Corporation';
      case 'partnership':
        return 'Partnership';
      case 'trust':
        return 'Trust/estate';
      case 'llc':
        return `Limited liability company (${llcClassification})`;
      case 'other':
        return `Other: ${otherDescription}`;
      default:
        return classification;
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white">
      {/* Form Header */}
      <div className="text-center mb-6 pb-4 border-b-2 border-gray-900">
        <h1 className="text-2xl font-bold">Form W-9</h1>
        <p className="text-lg">(Rev. October 2018)</p>
        <p className="text-lg font-semibold">Request for Taxpayer Identification Number and Certification</p>
        <p className="text-sm mt-2">Department of the Treasury - Internal Revenue Service</p>
      </div>

      {/* Part I: Name and Business */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4 bg-gray-100 p-2">Part I: Name and Business</h2>
        
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              1. Name (as shown on your income tax return)
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
              {getFieldValue('name', 'name')}
            </div>
          </div>

          {getFieldValue('businessName', 'business_name', '') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                2. Business name/disregarded entity name, if different from above
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                {getFieldValue('businessName', 'business_name')}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              3. Federal tax classification
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
              {getTaxClassificationDisplay()}
            </div>
          </div>

          {(parsedData.exemptPayeeCode || parsedData.exemptionFromFatca) && (
            <div className="grid grid-cols-2 gap-4">
              {parsedData.exemptPayeeCode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    4. Exemptions (codes apply only to certain entities)
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    Exempt payee code: {parsedData.exemptPayeeCode}
                  </div>
                </div>
              )}
              {parsedData.exemptionFromFatca && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exemption from FATCA reporting code
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    {parsedData.exemptionFromFatca}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Part II: Address */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4 bg-gray-100 p-2">Part II: Address</h2>
        
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              5. Address (number, street, and apt. or suite no.)
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
              {getFieldValue('address', 'address') || 'Not provided'}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                6. City
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                {getFieldValue('city', 'city')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                {getFieldValue('state', 'state')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP code
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                {getFieldValue('zipCode', 'zip_code')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Requester Information */}
      {(parsedData.requesterName || parsedData.requesterAddress || parsedData.accountNumbers) && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4 bg-gray-100 p-2">Requester Information</h2>
          
          <div className="space-y-4">
            {parsedData.accountNumbers && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  7. List account number(s) here (optional)
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {parsedData.accountNumbers}
                </div>
              </div>
            )}
            {parsedData.requesterName && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requester Name
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {parsedData.requesterName}
                </div>
              </div>
            )}
            {parsedData.requesterAddress && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requester Address
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {parsedData.requesterAddress}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Part III: Taxpayer Identification Number (TIN) */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4 bg-gray-100 p-2">Part III: Taxpayer Identification Number (TIN)</h2>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Taxpayer Identification Number
          </label>
          <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono">
            {getTinDisplay()}
          </div>
        </div>
      </div>

      {/* Part IV: Certification */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4 bg-gray-100 p-2">Part IV: Certification</h2>
        
        <div className="space-y-3">
          <p className="text-sm">
            Under penalties of perjury, I certify that:
          </p>
          
          <div className="space-y-2 ml-4">
            <div className="flex items-start">
              <div className="w-4 h-4 border border-gray-400 rounded mr-3 mt-1 flex items-center justify-center bg-gray-50">
                {parsedData.certification1 && <span className="text-green-600">✓</span>}
              </div>
              <span className="text-sm">
                1. The number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me); and
              </span>
            </div>

            <div className="flex items-start">
              <div className="w-4 h-4 border border-gray-400 rounded mr-3 mt-1 flex items-center justify-center bg-gray-50">
                {parsedData.certification2 && <span className="text-green-600">✓</span>}
              </div>
              <span className="text-sm">
                2. I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding; and
              </span>
            </div>

            <div className="flex items-start">
              <div className="w-4 h-4 border border-gray-400 rounded mr-3 mt-1 flex items-center justify-center bg-gray-50">
                {parsedData.certification3 && <span className="text-green-600">✓</span>}
              </div>
              <span className="text-sm">
                3. I am a U.S. citizen or other U.S. person (defined below); and
              </span>
            </div>

            <div className="flex items-start">
              <div className="w-4 h-4 border border-gray-400 rounded mr-3 mt-1 flex items-center justify-center bg-gray-50">
                {parsedData.certification4 && <span className="text-green-600">✓</span>}
              </div>
              <span className="text-sm">
                4. The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Signature
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono">
              {parsedData.signature || 'Not provided'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
              {parsedData.signatureDate ? new Date(parsedData.signatureDate).toLocaleDateString() : 'Not provided'}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500 mt-8 pt-4 border-t">
        <p>Form W-9 (Rev. 10-2018) - Cat. No. 10231X - Department of the Treasury - Internal Revenue Service</p>
      </div>
    </div>
  );
};

export default W9Viewer;
